import axios from 'axios';
import { handleSessionExpired } from './authExpiry';

// Global 401 handling for EVERY axios call (the many raw `axios.get/post` calls
// use the default instance and would otherwise bypass the apiClient interceptor
// below). On an expired/invalid token we bounce to login instead of surfacing
// the error. The auth endpoints are excluded inside handleSessionExpired.
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) handleSessionExpired(error.config?.url);
    return Promise.reject(error);
  }
);

// Ensure API_BASE_URL doesn't have double /api
const baseUrl = process.env.REACT_APP_API_URL || '';
export const API_BASE_URL = baseUrl.endsWith('/api') ? baseUrl.slice(0, -4) : baseUrl;

// Debug logging
console.log('🔧 API Configuration:');
console.log('  - REACT_APP_API_URL:', process.env.REACT_APP_API_URL);
console.log('  - Base URL:', baseUrl);
console.log('  - Final API_BASE_URL:', API_BASE_URL);
console.log('  - Login URL will be:', `${API_BASE_URL}/api/auth/login`);

// Create axios client with base configuration
export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth interceptor
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Add response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) handleSessionExpired(error.config?.url);
    return Promise.reject(error);
  }
);

export const API_ENDPOINTS = {
  // Auth
  LOGIN: '/api/auth/login',
  REGISTER: '/api/auth/register',
  PROFILE: '/api/auth/profile',
  UPDATE_PROFILE: '/api/auth/profile',
  CHANGE_PASSWORD: '/api/auth/change-password',
  FORGOT_PASSWORD: '/api/auth/forgot-password',
  RESET_PASSWORD: '/api/auth/reset-password',
  VERIFY_RESET_TOKEN: (token) => `/api/auth/verify-reset-token/${token}`,
  UPLOAD_PROFILE_PICTURE: '/api/auth/profile/picture',
  DELETE_PROFILE_PICTURE: '/api/auth/profile/picture',
  
  // Customers
  CUSTOMERS: '/api/customers',
  CUSTOMER_DETAIL: (id) => `/api/customers/${id}`,
  CUSTOMER_ADDRESSES: (id) => `/api/customers/${id}/addresses`,
  
  // Contracts
  CONTRACTS: '/api/contracts',
  CONTRACT_DETAIL: (id) => `/api/contracts/${id}`,
  CONTRACT_COPY: (id) => `/api/contracts/${id}/copy`,
  CONTRACT_PRICES: (id) => `/api/contracts/${id}/prices`,
  CONTRACT_ALIASES: (id) => `/api/contracts/${id}/aliases`,
  CONTRACT_TOLERANCES: (id) => `/api/contracts/${id}/tolerances`,
  CUSTOMER_CONTRACTS: (customerId) => `/api/contracts/customer/${customerId}`,
  
  // Chargebacks
  CHARGEBACKS: '/api/chargebacks',
  CHARGEBACK_DETAIL: (id) => `/api/chargebacks/${id}`,
  CHARGEBACK_RECONCILE: (id) => `/api/chargebacks/${id}/reconcile`,
  CHARGEBACK_BULK_STATUS: '/api/chargebacks/bulk/status',
  CUSTOMER_CHARGEBACKS: (customerId) => `/api/chargebacks/customer/${customerId}`,
  CHARGEBACK_REJECT: (id) => `/api/chargebacks/${id}/reject`,
  CHARGEBACK_VALIDATE: (id) => `/api/chargebacks/${id}/validate`,
  REJECTION_REASONS: '/api/chargebacks/rejection-reasons',
  REJECTION_REASONS_SUBCATEGORIES: '/api/chargebacks/rejection-reasons/subcategories',

  // Line items
  LINE_ITEM_CLAIM: (claimId) => `/api/line-items/claim/${claimId}`,
  LINE_ITEM_BATCH: (batchId) => `/api/line-items/batch/${batchId}`,
  LINE_ITEM_DETAIL: (lineItemId) => `/api/line-items/${lineItemId}`,
  LINE_ITEM_VALIDATE: (lineItemId) => `/api/line-items/${lineItemId}/validate`,

  // Batches
  BATCHES: '/api/batches',
  BATCH_DETAIL: (id) => `/api/batches/${id}`,
  BATCH_STATUS: (id) => `/api/batches/${id}/status`,
  BATCH_PROCESS: (id) => `/api/batches/${id}/process`,
  BATCH_STATS: (id) => `/api/batches/${id}/stats`,
  
  // Dashboard
  DASHBOARD_STATS: '/api/dashboard/stats',
  
  // Upload
  UPLOAD_EDI: '/api/upload/edi844',
  UPLOAD_EXCEL: '/api/upload/excel',
  UPLOAD_HISTORY: '/api/upload/history',
  UPLOAD_EDI844_CSV: '/api/upload/edi844-csv',
  DOWNLOAD_EDI844_TEMPLATE: '/api/upload/edi844-template',
  UPLOAD_CONTRACTS_CSV: '/api/upload/contracts-csv',
  DOWNLOAD_CONTRACT_TEMPLATE: '/api/upload/contract-template',
  
  // Products
  PRODUCTS: '/api/products',
  PRODUCT_DETAIL: (id) => `/api/products/${id}`,
  PRODUCT_SEARCH_NDC: (ndc) => `/api/products/search/ndc/${ndc}`,
  PRODUCT_STATS: '/api/products/stats/overview',
  PRODUCT_FORMS: '/api/products/forms/list',
  PRODUCT_UNITS: '/api/products/units/list',
  PRODUCT_STATUSES: '/api/products/statuses/list',
  PRODUCT_MANUFACTURERS: '/api/products/manufacturers/list',
  
  // Admin
  ADMIN_STATS: '/api/admin/stats',
  ADMIN_USERS: '/api/admin/users',
  ADMIN_LOGS: '/api/admin/logs',
  ADMIN_ERRORS: '/api/admin/errors',
  ADMIN_CLEANUP: '/api/admin/maintenance/cleanup',
  ADMIN_EXPORT: (type) => `/api/admin/export/${type}`,
  
  // Reports
  REPORTS: '/api/reports',
  REPORTS_CHARGEBACK_SUMMARY: '/api/reports/chargeback-summary',
  REPORTS_CUSTOMER_ANALYSIS: '/api/reports/customer-analysis',
  REPORTS_CONTRACT_PERFORMANCE: '/api/reports/contract-performance',
  REPORTS_BATCH_PROCESSING: '/api/reports/batch-processing',
  REPORTS_SYSTEM_AUDIT: '/api/reports/system-audit',
  
  // EDI
  EDI_DASHBOARD: '/api/edi',
  EDI_PROCESS_844: '/api/edi/process-844',
  EDI_PROCESS_849: '/api/edi/process-849',
  EDI_GENERATE_845: '/api/edi/generate-845',
  EDI_FILES: '/api/edi/files',
  EDI_FILES_DETAIL: (id) => `/api/edi/files/${id}`,
  EDI_BATCHES: '/api/edi/batches',
  EDI_BATCHES_DETAIL: (id) => `/api/edi/batches/${id}`,
  EDI_BATCH_STATUS: (id) => `/api/edi/batches/${id}/status`,
  
  // AMP (Average Manufacturer Price)
  AMP_CALCULATE: (productId) => `/api/amp/calculate/${productId}`,
  AMP_CALCULATE_BULK: '/api/amp/calculate/bulk',
  AMP_REPORT: (productId) => `/api/amp/report/${productId}`,
  AMP_HISTORY: (productId) => `/api/amp/history/${productId}`,
  AMP_STATS: '/api/amp/stats',
  AMP_VALIDATE: '/api/amp/validate',
  AMP_CONFIG: '/api/amp/config',
  AMP_EXPORT: '/api/amp/export',
  
  // Sales Data
  SALES_DATA_IMPORT: '/api/sales-data/import',
  SALES_DATA_FILES: '/api/sales-data/files',
  SALES_DATA_FILE_DETAIL: (id) => `/api/sales-data/files/${id}`,
  SALES_DATA_RECORDS: '/api/sales-data/records',
  SALES_DATA_STATISTICS: '/api/sales-data/statistics',
  SALES_DATA_TEMPLATE: '/api/sales-data/template',
  SALES_DATA_VALIDATE_CHARGEBACK: '/api/sales-data/validate-chargeback',
  SALES_DATA_VALIDATE_CHARGEBACKS_BATCH: '/api/sales-data/validate-chargebacks-batch',
  SALES_DATA_VALIDATION_HISTORY: '/api/sales-data/validation-history',
  SALES_DATA_VALIDATION_STATISTICS: '/api/sales-data/validation-statistics',
  GPOS: '/api/gpos',
};

export const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    'Authorization': token ? `Bearer ${token}` : '',
  };
};

export const handleApiError = (error) => {
  if (error.response) {
    // Server responded with error status
    const message = error.response.data?.error || error.response.data?.message || 'An error occurred';
    return { message, status: error.response.status };
  } else if (error.request) {
    // Request made but no response
    return { message: 'No response from server. Please check your connection.', status: 0 };
  } else {
    // Something else happened
    return { message: error.message || 'An unexpected error occurred', status: 0 };
  }
};

// API Functions

// Customer API functions
export const getCustomers = async (params = {}) => {
  const queryParams = new URLSearchParams();
  
  if (params.page) queryParams.append('page', params.page);
  if (params.limit) queryParams.append('limit', params.limit);
  if (params.search) queryParams.append('search', params.search);
  if (params.type) queryParams.append('type', params.type);
  if (params.entityType) queryParams.append('entityType', params.entityType);
  if (params.status) queryParams.append('status', params.status);
  
  const response = await axios.get(`${API_BASE_URL}${API_ENDPOINTS.CUSTOMERS}?${queryParams}`, {
    headers: getAuthHeaders()
  });
  
  // Handle new API response format
  if (response.data.success && response.data.data) {
    return response.data.data;
  }
  return response.data;
};

export const uploadCustomersCSV = async (file) => {
  const formData = new FormData();
  formData.append('csvFile', file);
  
  const response = await axios.post(`${API_BASE_URL}${API_ENDPOINTS.CUSTOMERS}/upload-csv`, formData, {
    headers: {
      ...getAuthHeaders(),
      'Content-Type': 'multipart/form-data',
    },
  });
  
  return response.data;
};

export const downloadCustomerTemplate = async () => {
  const response = await axios.get(`${API_BASE_URL}${API_ENDPOINTS.CUSTOMERS}/download-template`, {
    headers: getAuthHeaders(),
    responseType: 'blob',
  });
  
  const url = window.URL.createObjectURL(new Blob([response.data]));
  const a = document.createElement('a');
  a.href = url;
  a.download = 'customer-upload-template.csv';
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
  document.body.removeChild(a);
};

// EDI 844 CSV API functions
export const uploadEDI844CSV = async (file) => {
  const formData = new FormData();
  formData.append('csvFile', file);
  
  const response = await axios.post(`${API_BASE_URL}${API_ENDPOINTS.UPLOAD_EDI844_CSV}`, formData, {
    headers: {
      ...getAuthHeaders(),
      'Content-Type': 'multipart/form-data',
    },
  });
  
  return response.data;
};

export const downloadEDI844Template = async () => {
  const response = await axios.get(`${API_BASE_URL}${API_ENDPOINTS.DOWNLOAD_EDI844_TEMPLATE}`, {
    headers: getAuthHeaders(),
    responseType: 'blob',
  });
  
  const url = window.URL.createObjectURL(new Blob([response.data]));
  const a = document.createElement('a');
  a.href = url;
  a.download = 'edi844-upload-template.csv';
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
  document.body.removeChild(a);
};

export const getCustomer = async (id) => {
  const response = await axios.get(`${API_BASE_URL}${API_ENDPOINTS.CUSTOMER_DETAIL(id)}`, {
    headers: getAuthHeaders()
  });
  if (response.data.success && response.data.data) {
    return response.data.data;
  }
  return response.data;
};

// Contract API functions
export const getContractAdminFees = async (id) => {
  const r = await axios.get(`${API_BASE_URL}/api/contracts/${id}/admin-fee`, { headers: getAuthHeaders() });
  return (r.data && r.data.data) || r.data;
};

export const accrueContractAdminFee = async (id, payload) => {
  const r = await axios.post(`${API_BASE_URL}/api/contracts/${id}/admin-fee/accrue`, payload, { headers: getAuthHeaders() });
  return (r.data && r.data.data) || r.data;
};

// Commercial / managed-care rebate program API functions
export const getRebatePrograms = async () => {
  const r = await axios.get(`${API_BASE_URL}/api/rebates`, { headers: getAuthHeaders() });
  return (r.data && r.data.data) || [];
};

export const getRebateProgram = async (id) => {
  const r = await axios.get(`${API_BASE_URL}/api/rebates/${id}`, { headers: getAuthHeaders() });
  return (r.data && r.data.data) || r.data;
};

export const createRebateProgram = async (payload) => {
  const r = await axios.post(`${API_BASE_URL}/api/rebates`, payload, { headers: getAuthHeaders() });
  return (r.data && r.data.data) || r.data;
};

export const updateRebateProgram = async (id, payload) => {
  const r = await axios.put(`${API_BASE_URL}/api/rebates/${id}`, payload, { headers: getAuthHeaders() });
  return (r.data && r.data.data) || r.data;
};

export const deleteRebateProgram = async (id) => {
  const r = await axios.delete(`${API_BASE_URL}/api/rebates/${id}`, { headers: getAuthHeaders() });
  return r.data;
};

export const addRebateTier = async (id, payload) => {
  const r = await axios.post(`${API_BASE_URL}/api/rebates/${id}/tiers`, payload, { headers: getAuthHeaders() });
  return (r.data && r.data.data) || r.data;
};

export const deleteRebateTier = async (id, tierId) => {
  const r = await axios.delete(`${API_BASE_URL}/api/rebates/${id}/tiers/${tierId}`, { headers: getAuthHeaders() });
  return r.data;
};

export const getRebateUtilization = async (id) => {
  const r = await axios.get(`${API_BASE_URL}/api/rebates/${id}/utilization`, { headers: getAuthHeaders() });
  return (r.data && r.data.data) || [];
};

export const importRebateUtilization = async (id, file) => {
  const formData = new FormData();
  formData.append('csvFile', file);
  const r = await axios.post(`${API_BASE_URL}/api/rebates/${id}/utilization-import`, formData, {
    headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' },
  });
  return r.data;
};

export const accrueRebate = async (id, payload) => {
  const r = await axios.post(`${API_BASE_URL}/api/rebates/${id}/accrue`, payload, { headers: getAuthHeaders() });
  return r.data;
};

export const getRebateStatement = async (id) => {
  const r = await axios.get(`${API_BASE_URL}/api/rebates/${id}/statement`, { headers: getAuthHeaders() });
  return (r.data && r.data.data) || r.data;
};

// Gross-to-Net analytics
export const getGtnSummary = async ({ periodStart, periodEnd, contractId } = {}) => {
  const params = new URLSearchParams();
  if (periodStart) params.set('periodStart', periodStart);
  if (periodEnd) params.set('periodEnd', periodEnd);
  if (contractId) params.set('contractId', contractId);
  const r = await axios.get(`${API_BASE_URL}/api/gtn/summary?${params.toString()}`, { headers: getAuthHeaders() });
  return (r.data && r.data.data) || r.data;
};

export const getContracts = async () => {
  const response = await axios.get(`${API_BASE_URL}${API_ENDPOINTS.CONTRACTS}`, {
    headers: getAuthHeaders()
  });
  if (response.data.success && response.data.data) {
    return response.data.data;
  }
  return response.data;
};

export const getContract = async (id) => {
  const response = await axios.get(`${API_BASE_URL}${API_ENDPOINTS.CONTRACT_DETAIL(id)}`, {
    headers: getAuthHeaders()
  });
  // Handle both response formats
  if (response.data.success && response.data.data) {
    return response.data.data;
  }
  if (response.data.contract) {
    return response.data.contract;
  }
  return response.data;
};

export const createContract = async (contractData) => {
  const response = await axios.post(`${API_BASE_URL}${API_ENDPOINTS.CONTRACTS}`, contractData, {
    headers: getAuthHeaders()
  });
  // Handle both response formats
  if (response.data.success && response.data.data) {
    return response.data.data;
  }
  if (response.data.contract) {
    return response.data.contract;
  }
  return response.data;
};

export const updateContract = async ({ id, contractData }) => {
  const response = await axios.put(`${API_BASE_URL}${API_ENDPOINTS.CONTRACT_DETAIL(id)}`, contractData, {
    headers: getAuthHeaders()
  });
  // Handle both response formats
  if (response.data.success && response.data.data) {
    return response.data.data;
  }
  if (response.data.contract) {
    return response.data.contract;
  }
  return response.data;
};

export const copyContract = async (id, copyData) => {
  const response = await axios.post(`${API_BASE_URL}${API_ENDPOINTS.CONTRACT_COPY(id)}`, copyData, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const deleteContract = async (id) => {
  const response = await axios.delete(`${API_BASE_URL}${API_ENDPOINTS.CONTRACT_DETAIL(id)}`, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const createCustomer = async (customerData) => {
  const response = await axios.post(`${API_BASE_URL}${API_ENDPOINTS.CUSTOMERS}`, customerData, {
    headers: getAuthHeaders()
  });
  // Handle new API response format
  if (response.data.success && response.data.data) {
    return response.data.data;
  }
  return response.data;
};

export const updateCustomer = async (id, customerData) => {
  const response = await axios.put(`${API_BASE_URL}${API_ENDPOINTS.CUSTOMER_DETAIL(id)}`, customerData, {
    headers: getAuthHeaders()
  });
  // Handle new API response format
  if (response.data.success && response.data.data) {
    return response.data.data;
  }
  return response.data;
};

export const deleteCustomer = async (id) => {
  const response = await axios.delete(`${API_BASE_URL}${API_ENDPOINTS.CUSTOMER_DETAIL(id)}`, {
    headers: getAuthHeaders()
  });
  // Handle new API response format
  if (response.data.success && response.data.data) {
    return response.data.data;
  }
  return response.data;
};

// Chargeback API functions
export const getChargebacks = async (params = {}) => {
  const queryParams = new URLSearchParams();
  
  if (params.page) queryParams.append('page', params.page);
  if (params.limit) queryParams.append('limit', params.limit);
  if (params.search) queryParams.append('search', params.search);
  if (params.status) queryParams.append('status', params.status);
  if (params.customerId) queryParams.append('customerId', params.customerId);
  if (params.batchId) queryParams.append('batchId', params.batchId);
  if (params.uploadedFrom) queryParams.append('uploadedFrom', params.uploadedFrom);
  if (params.uploadedTo) queryParams.append('uploadedTo', params.uploadedTo);
  if (params.dateRange) queryParams.append('dateRange', params.dateRange);
  if (params.sortBy) queryParams.append('sortBy', params.sortBy);
  if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder);
  if (params.includeRejectionDetails) queryParams.append('includeRejectionDetails', 'true');
  if (params.archived) queryParams.append('archived', 'true');
  if (params.includeArchived) queryParams.append('includeArchived', 'true');

  const url = `${API_BASE_URL}${API_ENDPOINTS.CHARGEBACKS}${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
  
  const response = await axios.get(url, {
    headers: getAuthHeaders()
  });
  // Handle new API response format
  if (response.data.success && response.data.data) {
    return response.data.data;
  }
  return response.data;
};

export const getChargeback = async (id) => {
  const response = await axios.get(`${API_BASE_URL}${API_ENDPOINTS.CHARGEBACK_DETAIL(id)}`, {
    headers: getAuthHeaders()
  });
  // Handle new API response format
  if (response.data.success && response.data.data) {
    return response.data.data;
  }
  return response.data;
};

export const createChargeback = async (chargebackData) => {
  const response = await axios.post(`${API_BASE_URL}${API_ENDPOINTS.CHARGEBACKS}`, chargebackData, {
    headers: getAuthHeaders()
  });
  // Handle new API response format
  if (response.data.success && response.data.data) {
    return response.data.data;
  }
  return response.data;
};

export const updateChargeback = async (id, chargebackData) => {
  const response = await axios.put(`${API_BASE_URL}${API_ENDPOINTS.CHARGEBACK_DETAIL(id)}`, chargebackData, {
    headers: getAuthHeaders()
  });
  if (response.data.success && response.data.data) {
    return response.data.data;
  }
  return response.data;
};

export const downloadChargebackReport = async (id) => {
  const response = await axios.get(`${API_BASE_URL}/api/chargebacks/${id}/download`, {
    headers: getAuthHeaders(),
    responseType: 'blob'
  });
  return response.data;
};

export const reconcileChargeback = async (id, reconciliationData) => {
  const response = await axios.post(`${API_BASE_URL}${API_ENDPOINTS.CHARGEBACK_RECONCILE(id)}`, reconciliationData, {
    headers: getAuthHeaders()
  });
  if (response.data.success && response.data.data) {
    return response.data.data;
  }
  return response.data;
};

// Role Management API functions
export const getRoles = async () => {
  const response = await axios.get(`${API_BASE_URL}/api/admin/roles`, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const createRole = async (roleData) => {
  const response = await axios.post(`${API_BASE_URL}/api/admin/roles`, roleData, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const updateRole = async ({ id, ...roleData }) => {
  const response = await axios.put(`${API_BASE_URL}/api/admin/roles/${id}`, roleData, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const deleteRole = async (id) => {
  const response = await axios.delete(`${API_BASE_URL}/api/admin/roles/${id}`, {
    headers: getAuthHeaders()
  });
  return response.data;
};

// Reports API functions
export const getReports = async () => {
  const response = await axios.get(`${API_BASE_URL}/api/reports`, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const getChargebackSummaryReport = async (params = {}) => {
  const queryParams = new URLSearchParams();
  if (params.startDate) queryParams.append('startDate', params.startDate);
  if (params.endDate) queryParams.append('endDate', params.endDate);
  if (params.customerId) queryParams.append('customerId', params.customerId);
  if (params.status) queryParams.append('status', params.status);
  
  const response = await axios.get(`${API_BASE_URL}/api/reports/chargeback-summary?${queryParams}`, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const getCustomerAnalysisReport = async (params = {}) => {
  const queryParams = new URLSearchParams();
  if (params.startDate) queryParams.append('startDate', params.startDate);
  if (params.endDate) queryParams.append('endDate', params.endDate);
  
  const response = await axios.get(`${API_BASE_URL}/api/reports/customer-analysis?${queryParams}`, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const getContractPerformanceReport = async () => {
  const response = await axios.get(`${API_BASE_URL}/api/reports/contract-performance`, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const getBatchProcessingReport = async (params = {}) => {
  const queryParams = new URLSearchParams();
  if (params.startDate) queryParams.append('startDate', params.startDate);
  if (params.endDate) queryParams.append('endDate', params.endDate);
  
  const response = await axios.get(`${API_BASE_URL}/api/reports/batch-processing?${queryParams}`, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const getSystemAuditReport = async (params = {}) => {
  const queryParams = new URLSearchParams();
  if (params.startDate) queryParams.append('startDate', params.startDate);
  if (params.endDate) queryParams.append('endDate', params.endDate);
  
  const response = await axios.get(`${API_BASE_URL}/api/reports/system-audit?${queryParams}`, {
    headers: getAuthHeaders()
  });
  return response.data;
};

// EDI API functions
export const getEDIDashboard = async () => {
  const response = await axios.get(`${API_BASE_URL}/api/edi`, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const processEDI844 = async (file, customerId, description) => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('customerId', customerId);
  if (description) formData.append('description', description);
  
  const response = await axios.post(`${API_BASE_URL}/api/edi/process-844`, formData, {
    headers: {
      ...getAuthHeaders(),
      'Content-Type': 'multipart/form-data',
    }
  });
  return response.data;
};

export const processEDI849 = async (file, customerId, description) => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('customerId', customerId);
  if (description) formData.append('description', description);
  
  const response = await axios.post(`${API_BASE_URL}/api/edi/process-849`, formData, {
    headers: {
      ...getAuthHeaders(),
      'Content-Type': 'multipart/form-data',
    }
  });
  return response.data;
};

export const generateEDI845 = async (contractId, customerId, chargebackIds) => {
  const response = await axios.post(`${API_BASE_URL}/api/edi/generate-845`, {
    contractId,
    customerId,
    chargebackIds
  }, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const getEDIFiles = async (params = {}) => {
  const queryParams = new URLSearchParams();
  if (params.page) queryParams.append('page', params.page);
  if (params.limit) queryParams.append('limit', params.limit);
  if (params.status) queryParams.append('status', params.status);
  
  const response = await axios.get(`${API_BASE_URL}/api/edi/files?${queryParams}`, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const getEDIFileDetail = async (id) => {
  const response = await axios.get(`${API_BASE_URL}/api/edi/files/${id}`, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const getEDIBatches = async (params = {}) => {
  const queryParams = new URLSearchParams();
  if (params.page) queryParams.append('page', params.page);
  if (params.limit) queryParams.append('limit', params.limit);
  if (params.status) queryParams.append('status', params.status);
  
  const response = await axios.get(`${API_BASE_URL}/api/edi/batches?${queryParams}`, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const getEDIBatchDetail = async (id) => {
  const response = await axios.get(`${API_BASE_URL}/api/edi/batches/${id}`, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const updateEDIBatchStatus = async (id, status, notes) => {
  const response = await axios.post(`${API_BASE_URL}/api/edi/batches/${id}/status`, {
    status,
    notes
  }, {
    headers: getAuthHeaders()
  });
  return response.data;
};

// Batch API functions
export const getBatches = async (params = {}) => {
  const queryParams = new URLSearchParams();
  
  if (params.page) queryParams.append('page', params.page);
  if (params.limit) queryParams.append('limit', params.limit);
  if (params.search) queryParams.append('search', params.search);
  if (params.status) queryParams.append('status', params.status);
  if (params.archived) queryParams.append('archived', params.archived);
  if (params.startDate) queryParams.append('startDate', params.startDate);
  if (params.endDate) queryParams.append('endDate', params.endDate);

  const url = `${API_BASE_URL}${API_ENDPOINTS.BATCHES}${queryParams.toString() ? '?' + queryParams.toString() : ''}`;

  const response = await axios.get(url, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const archiveBatch = async (id) => {
  const r = await axios.post(`${API_BASE_URL}/api/batches/${id}/archive`, {}, { headers: getAuthHeaders() });
  return r.data;
};
export const unarchiveBatch = async (id) => {
  const r = await axios.post(`${API_BASE_URL}/api/batches/${id}/unarchive`, {}, { headers: getAuthHeaders() });
  return r.data;
};

export const getBatchDetail = async (id) => {
  const response = await axios.get(`${API_BASE_URL}${API_ENDPOINTS.BATCHES}/${id}`, {
    headers: getAuthHeaders()
  });
  
  // Handle new API response format
  if (response.data.success && response.data.data) {
    return response.data.data;
  }
  return response.data;
};

// Line Items API functions
export const getBatchLineItems = async (batchId, params = {}) => {
  const queryParams = new URLSearchParams();
  
  if (params.page) queryParams.append('page', params.page);
  if (params.limit) queryParams.append('limit', params.limit);
  if (params.status) queryParams.append('status', params.status);
  if (params.validationStatus) queryParams.append('validationStatus', params.validationStatus);
  
  const url = `${API_BASE_URL}/api/line-items/batch/${batchId}${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
  
  const response = await axios.get(url, {
    headers: getAuthHeaders()
  });
  
  // Handle new API response format
  if (response.data.success && response.data.data) {
    return response.data.data;
  }
  return response.data;
};

export const getLineItemDetail = async (id) => {
  const response = await axios.get(`${API_BASE_URL}/api/line-items/${id}`, {
    headers: getAuthHeaders()
  });
  
  // Handle new API response format
  if (response.data.success && response.data.data) {
    return response.data.data;
  }
  return response.data;
};

export const updateLineItem = async ({ id, ...data }) => {
  const response = await axios.put(`${API_BASE_URL}/api/line-items/${id}`, data, {
    headers: getAuthHeaders()
  });
  
  // Handle new API response format
  if (response.data.success && response.data.data) {
    return response.data.data;
  }
  return response.data;
};

export const getLineItemDetails = async (lineItemId) => {
  const response = await axios.get(`${API_BASE_URL}/api/line-items/${lineItemId}`, {
    headers: getAuthHeaders()
  });
  
  // Handle new API response format
  if (response.data.success && response.data.data) {
    return response.data.data;
  }
  return response.data;
};

export const validateLineItem = async (id) => {
  const response = await axios.post(`${API_BASE_URL}/api/line-items/${id}/validate`, {}, {
    headers: getAuthHeaders()
  });
  
  // Handle new API response format
  if (response.data.success && response.data.data) {
    return response.data.data;
  }
  return response.data;
};

export const reprocessBatch = async (batchId) => {
  const response = await axios.post(`${API_BASE_URL}/api/line-items/batch/${batchId}/reprocess`, {}, {
    headers: getAuthHeaders()
  });
  
  // Handle new API response format
  if (response.data.success && response.data.data) {
    return response.data.data;
  }
  return response.data;
};

export const downloadBatchReport = async (id) => {
  const response = await axios.get(`${API_BASE_URL}/api/batches/${id}/download`, {
    headers: getAuthHeaders(),
    responseType: 'blob'
  });
  return response.data;
};

export const processBatch = async (id, action, notes) => {
  const response = await axios.post(`${API_BASE_URL}/api/batches/${id}/process`, {
    action,
    notes
  }, {
    headers: getAuthHeaders()
  });
  if (response.data.success && response.data.data) {
    return response.data.data;
  }
  return response.data;
};

// Dashboard API functions
export const getDashboardStats = async () => {
  const response = await axios.get(`${API_BASE_URL}${API_ENDPOINTS.DASHBOARD_STATS}`, {
    headers: getAuthHeaders()
  });
  // Handle new API response format
  if (response.data.success && response.data.data) {
    return response.data.data;
  }
  return response.data;
};

// Upload API functions
export const uploadFile = async (formData) => {
  const response = await axios.post(`${API_BASE_URL}/api/upload/file`, formData, {
    headers: {
      ...getAuthHeaders(),
      'Content-Type': 'multipart/form-data',
    }
  });
  // Handle new API response format
  if (response.data.success && response.data.data) {
    return response.data.data;
  }
  return response.data;
};

// Product API functions
export const getProducts = async (params = {}) => {
  const queryParams = new URLSearchParams();
  
  if (params.page) queryParams.append('page', params.page);
  if (params.limit) queryParams.append('limit', params.limit);
  if (params.search) queryParams.append('search', params.search);
  if (params.status) queryParams.append('status', params.status);
  if (params.form) queryParams.append('form', params.form);
  if (params.medicaid !== undefined) queryParams.append('medicaid', params.medicaid);
  if (params.sortBy) queryParams.append('sortBy', params.sortBy);
  if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder);
  
  const url = `${API_BASE_URL}${API_ENDPOINTS.PRODUCTS}${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
  
  const response = await axios.get(url, {
    headers: getAuthHeaders()
  });
  // Handle API response format
  if (response.data.success && response.data.data) {
    return response.data.data;
  }
  // Handle direct response format (products API returns products directly)
  return response.data;
};

export const getProduct = async (id) => {
  const response = await axios.get(`${API_BASE_URL}${API_ENDPOINTS.PRODUCT_DETAIL(id)}`, {
    headers: getAuthHeaders()
  });
  // Handle new API response format
  if (response.data.success && response.data.data) {
    return response.data.data;
  }
  return response.data;
};

export const createProduct = async (productData) => {
  const response = await axios.post(`${API_BASE_URL}${API_ENDPOINTS.PRODUCTS}`, productData, {
    headers: getAuthHeaders()
  });
  // Handle new API response format
  if (response.data.success && response.data.data) {
    return response.data.data;
  }
  return response.data;
};

export const updateProduct = async (id, productData) => {
  const response = await axios.put(`${API_BASE_URL}${API_ENDPOINTS.PRODUCT_DETAIL(id)}`, productData, {
    headers: getAuthHeaders()
  });
  // Handle new API response format
  if (response.data.success && response.data.data) {
    return response.data.data;
  }
  return response.data;
};

export const deleteProduct = async (id) => {
  const response = await axios.delete(`${API_BASE_URL}${API_ENDPOINTS.PRODUCT_DETAIL(id)}`, {
    headers: getAuthHeaders()
  });
  // Handle new API response format
  if (response.data.success && response.data.data) {
    return response.data.data;
  }
  return response.data;
};

// Product dropdown data functions
export const getProductForms = async () => {
  const response = await axios.get(`${API_BASE_URL}${API_ENDPOINTS.PRODUCT_FORMS}`, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const getProductUnits = async () => {
  const response = await axios.get(`${API_BASE_URL}${API_ENDPOINTS.PRODUCT_UNITS}`, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const getProductStatuses = async () => {
  const response = await axios.get(`${API_BASE_URL}${API_ENDPOINTS.PRODUCT_STATUSES}`, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const getProductManufacturers = async () => {
  const response = await axios.get(`${API_BASE_URL}${API_ENDPOINTS.PRODUCT_MANUFACTURERS}`, {
    headers: getAuthHeaders()
  });
  return response.data;
};

// Contract CSV Upload functions
export const uploadContractsCSV = async (file) => {
  const formData = new FormData();
  formData.append('csvFile', file);
  
  const response = await axios.post(`${API_BASE_URL}${API_ENDPOINTS.UPLOAD_CONTRACTS_CSV}`, formData, {
    headers: {
      ...getAuthHeaders(),
      'Content-Type': 'multipart/form-data'
    }
  });
  return response.data;
};

export const downloadContractTemplate = async () => {
  const response = await axios.get(`${API_BASE_URL}${API_ENDPOINTS.DOWNLOAD_CONTRACT_TEMPLATE}`, {
    headers: getAuthHeaders(),
    responseType: 'blob'
  });
  return response.data;
};

// Contract Management API functions
export const getContractPricing = async (contractId) => {
  const response = await axios.get(`${API_BASE_URL}/api/contracts/${contractId}/pricing`, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const createContractPrice = async (contractId, priceData) => {
  const response = await axios.post(`${API_BASE_URL}/api/contracts/${contractId}/pricing`, priceData, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const updateContractPrice = async (contractId, priceId, priceData) => {
  const response = await axios.put(`${API_BASE_URL}/api/contracts/${contractId}/pricing/${priceId}`, priceData, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const deleteContractPrice = async (contractId, priceId) => {
  const response = await axios.delete(`${API_BASE_URL}/api/contracts/${contractId}/pricing/${priceId}`, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const deleteAllContractPricing = async (contractId) => {
  const response = await axios.delete(`${API_BASE_URL}/api/contracts/${contractId}/pricing`, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const getContractCustomers = async (contractId) => {
  const response = await axios.get(`${API_BASE_URL}/api/contracts/${contractId}/customers`, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const addContractCustomer = async (contractId, customerData) => {
  const response = await axios.post(`${API_BASE_URL}/api/contracts/${contractId}/customers`, customerData, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const updateContractCustomer = async (contractId, customerId, customerData) => {
  const response = await axios.put(`${API_BASE_URL}/api/contracts/${contractId}/customers/${customerId}`, customerData, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const removeContractCustomer = async (contractId, customerId) => {
  const response = await axios.delete(`${API_BASE_URL}/api/contracts/${contractId}/customers/${customerId}`, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const getContractAliases = async (contractId) => {
  const response = await axios.get(`${API_BASE_URL}/api/contracts/${contractId}/aliases`, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const createContractAlias = async (contractId, aliasData) => {
  const response = await axios.post(`${API_BASE_URL}/api/contracts/${contractId}/aliases`, aliasData, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const updateContractAlias = async (contractId, aliasId, aliasData) => {
  const response = await axios.put(`${API_BASE_URL}/api/contracts/${contractId}/aliases/${aliasId}`, aliasData, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const deleteContractAlias = async (contractId, aliasId) => {
  const response = await axios.delete(`${API_BASE_URL}/api/contracts/${contractId}/aliases/${aliasId}`, {
    headers: getAuthHeaders()
  });
  return response.data;
};

// Password Reset Functions
export const forgotPassword = async (email) => {
  const response = await axios.post(`${API_BASE_URL}${API_ENDPOINTS.FORGOT_PASSWORD}`, { email });
  return response.data;
};

export const resetPassword = async (token, password) => {
  const response = await axios.post(`${API_BASE_URL}${API_ENDPOINTS.RESET_PASSWORD}`, { token, password });
  return response.data;
};

export const verifyResetToken = async (token) => {
  const response = await axios.get(`${API_BASE_URL}${API_ENDPOINTS.VERIFY_RESET_TOKEN(token)}`);
  return response.data;
};

// Profile Functions
export const updateProfile = async (profileData) => {
  const response = await axios.put(`${API_BASE_URL}${API_ENDPOINTS.UPDATE_PROFILE}`, profileData, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const uploadProfilePicture = async (file) => {
  const formData = new FormData();
  formData.append('profilePicture', file);
  
  const response = await axios.post(`${API_BASE_URL}${API_ENDPOINTS.UPLOAD_PROFILE_PICTURE}`, formData, {
    headers: {
      ...getAuthHeaders(),
      'Content-Type': 'multipart/form-data'
    }
  });
  return response.data;
};

export const deleteProfilePicture = async () => {
  const response = await axios.delete(`${API_BASE_URL}${API_ENDPOINTS.DELETE_PROFILE_PICTURE}`, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const changePassword = async (passwordData) => {
  const response = await axios.post(`${API_BASE_URL}${API_ENDPOINTS.CHANGE_PASSWORD}`, passwordData, {
    headers: getAuthHeaders()
  });
  return response.data;
};

// AMP (Average Manufacturer Price) API functions
export const calculateProductAMP = async (productId, options = {}) => {
  const response = await axios.post(`${API_BASE_URL}${API_ENDPOINTS.AMP_CALCULATE(productId)}`, {
    options
  }, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const calculateBulkAMP = async (productIds, options = {}) => {
  const response = await axios.post(`${API_BASE_URL}${API_ENDPOINTS.AMP_CALCULATE_BULK}`, {
    productIds,
    options
  }, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const generateAMPReport = async (productId, options = {}) => {
  const queryParams = new URLSearchParams();
  Object.keys(options).forEach(key => {
    if (options[key] !== undefined && options[key] !== null) {
      queryParams.append(key, options[key]);
    }
  });
  
  const url = `${API_BASE_URL}${API_ENDPOINTS.AMP_REPORT(productId)}${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
  
  const response = await axios.get(url, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const getAMPHistory = async (productId, params = {}) => {
  const queryParams = new URLSearchParams();
  if (params.limit) queryParams.append('limit', params.limit);
  if (params.offset) queryParams.append('offset', params.offset);
  
  const url = `${API_BASE_URL}${API_ENDPOINTS.AMP_HISTORY(productId)}${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
  
  const response = await axios.get(url, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const getAMPStats = async (params = {}) => {
  const queryParams = new URLSearchParams();
  if (params.startDate) queryParams.append('startDate', params.startDate);
  if (params.endDate) queryParams.append('endDate', params.endDate);
  if (params.productIds) queryParams.append('productIds', params.productIds.join(','));
  
  const url = `${API_BASE_URL}${API_ENDPOINTS.AMP_STATS}${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
  
  const response = await axios.get(url, {
    headers: getAuthHeaders()
  });
  return response.data?.data ?? response.data;
};

export const validateAMPParameters = async (productIds, options = {}) => {
  const response = await axios.post(`${API_BASE_URL}${API_ENDPOINTS.AMP_VALIDATE}`, {
    productIds,
    options
  }, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const getAMPConfig = async () => {
  const response = await axios.get(`${API_BASE_URL}${API_ENDPOINTS.AMP_CONFIG}`, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const exportAMPCalculations = async (productIds, options = {}, format = 'csv') => {
  const response = await axios.post(`${API_BASE_URL}${API_ENDPOINTS.AMP_EXPORT}`, {
    productIds,
    options,
    format
  }, {
    headers: getAuthHeaders(),
    responseType: format === 'csv' ? 'blob' : 'json'
  });
  
  if (format === 'csv') {
    // Handle CSV download
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const a = document.createElement('a');
    a.href = url;
    a.download = `amp-calculations-${Date.now()}.csv`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
    return { success: true, message: 'CSV file downloaded successfully' };
  }
  
  return response.data;
};

// Sales Data API functions
export const importSalesData = async (file, options = {}) => {
  const formData = new FormData();
  formData.append('salesDataFile', file);
  formData.append('options', JSON.stringify(options));
  
  const response = await axios.post(`${API_BASE_URL}${API_ENDPOINTS.SALES_DATA_IMPORT}`, formData, {
    headers: {
      ...getAuthHeaders(),
      'Content-Type': 'multipart/form-data'
    }
  });
  return response.data;
};

// --- Sales-data column mapping ---
export const previewSalesFile = async (file) => {
  const fd = new FormData();
  fd.append('salesDataFile', file);
  const r = await axios.post(`${API_BASE_URL}/api/sales-data/preview`, fd, {
    headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' }
  });
  return r.data?.data || { headers: [], sampleRows: [], suggestedMapping: {}, fields: [] };
};
export const getSalesMappingProfiles = async () => {
  const r = await axios.get(`${API_BASE_URL}/api/sales-data/mapping-profiles`, { headers: getAuthHeaders() });
  return r.data?.data?.profiles || [];
};
export const saveSalesMappingProfile = async (payload) => {
  const r = await axios.post(`${API_BASE_URL}/api/sales-data/mapping-profiles`, payload, { headers: getAuthHeaders() });
  return r.data?.data;
};
export const deleteSalesMappingProfile = async (id) => {
  const r = await axios.delete(`${API_BASE_URL}/api/sales-data/mapping-profiles/${id}`, { headers: getAuthHeaders() });
  return r.data;
};

// Chargeback Processing API functions
export const getChargebackStats = async (params = {}) => {
  const response = await axios.get(`${API_BASE_URL}${API_ENDPOINTS.CHARGEBACKS}/stats`, {
    headers: getAuthHeaders(),
    params
  });
  return response.data;
};

export const exportChargebacksCsv = async (params = {}) => {
  const clean = Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined && v !== '' && v !== 'ALL'));
  const qs = new URLSearchParams(clean).toString();
  const r = await axios.get(`${API_BASE_URL}${API_ENDPOINTS.CHARGEBACKS}/export${qs ? `?${qs}` : ''}`, {
    headers: getAuthHeaders(), responseType: 'blob'
  });
  const cd = r.headers['content-disposition'] || '';
  const m = cd.match(/filename="?([^"]+)"?/);
  const name = (m && m[1]) || `chargebacks-export-${new Date().toISOString().slice(0, 10)}.csv`;
  const url = window.URL.createObjectURL(new Blob([r.data], { type: 'text/csv' }));
  const a = document.createElement('a');
  a.href = url; a.download = name;
  document.body.appendChild(a); a.click(); a.remove();
  window.URL.revokeObjectURL(url);
};

export const runAdjudication = async ({ batchId, generateMemos } = {}) => {
  const r = await axios.post(`${API_BASE_URL}${API_ENDPOINTS.CHARGEBACKS}/adjudicate`, { batchId: batchId || undefined, generateMemos: !!generateMemos }, { headers: getAuthHeaders() });
  return r.data?.data;
};

// ---- Admin audit trail ----
const cleanParams = (params = {}) =>
  Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '' && v !== 'ALL'));

export const getAuditLog = async (params = {}) => {
  const qs = new URLSearchParams(cleanParams(params)).toString();
  const r = await apiClient.get(`/api/admin/audit${qs ? `?${qs}` : ''}`);
  return r.data;
};

export const getAuditFilters = async () => {
  const r = await apiClient.get('/api/admin/audit/filters');
  return r.data;
};

export const exportAuditLog = async (params = {}) => {
  const qs = new URLSearchParams(cleanParams(params)).toString();
  const r = await axios.get(`${API_BASE_URL}/api/admin/audit/export${qs ? `?${qs}` : ''}`, {
    headers: getAuthHeaders(), responseType: 'blob'
  });
  const cd = r.headers['content-disposition'] || '';
  const m = cd.match(/filename="?([^"]+)"?/);
  const name = (m && m[1]) || `audit-log-${new Date().toISOString().slice(0, 10)}.csv`;
  const url = window.URL.createObjectURL(new Blob([r.data], { type: 'text/csv' }));
  const a = document.createElement('a');
  a.href = url; a.download = name;
  document.body.appendChild(a); a.click(); a.remove();
  window.URL.revokeObjectURL(url);
};

export const getChargebacksByBatch = async () => {
  const r = await axios.get(`${API_BASE_URL}${API_ENDPOINTS.CHARGEBACKS}/by-batch`, { headers: getAuthHeaders() });
  return r.data?.data || { batches: [], total: 0 };
};

export const getChargebackStatusCounts = async () => {
  const response = await axios.get(`${API_BASE_URL}${API_ENDPOINTS.CHARGEBACKS}/status-counts`, { headers: getAuthHeaders() });
  return response.data?.data || {};
};

export const getChargebackActivity = async (id) => {
  const response = await axios.get(`${API_BASE_URL}${API_ENDPOINTS.CHARGEBACKS}/${id}/activity`, { headers: getAuthHeaders() });
  return response.data?.data?.activity || [];
};

export const getChargebackDetail = async (id) => {
  const response = await axios.get(`${API_BASE_URL}${API_ENDPOINTS.CHARGEBACK_DETAIL(id)}`, {
    headers: getAuthHeaders()
  });
  // Handle new API response format
  if (response.data.success && response.data.data) {
    return response.data.data;
  }
  return response.data;
};

export const getChargebackLineItems = async (claimId) => {
  const response = await axios.get(`${API_BASE_URL}${API_ENDPOINTS.LINE_ITEM_CLAIM(claimId)}`, {
    headers: getAuthHeaders()
  });
  // Unwrap the { success, data: { lineItems, pagination } } envelope so the
  // editor can read lineItemsData.lineItems directly.
  return response.data?.data || response.data;
};

export const updateChargebackLineItem = async ({ lineItemId, data }) => {
  const response = await axios.put(`${API_BASE_URL}${API_ENDPOINTS.LINE_ITEM_DETAIL(lineItemId)}`, data, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const validateChargebackLineItem = async ({ lineItemId, data }) => {
  const response = await axios.post(`${API_BASE_URL}${API_ENDPOINTS.LINE_ITEM_VALIDATE(lineItemId)}`, data, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const validateChargeback = async ({ chargebackId, validationType }) => {
  const response = await axios.post(`${API_BASE_URL}${API_ENDPOINTS.CHARGEBACK_DETAIL(chargebackId)}/validate`, {
    validationType
  }, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const approveChargeback = async ({ chargebackId, notes, approvedBy }) => {
  const response = await axios.post(`${API_BASE_URL}${API_ENDPOINTS.CHARGEBACK_DETAIL(chargebackId)}/approve`, {
    notes,
    approvedBy
  }, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const rejectChargeback = async ({ chargebackId, rejectionReasonId, rejectedBy, rejectionDetails }) => {
  const response = await axios.post(`${API_BASE_URL}${API_ENDPOINTS.CHARGEBACK_REJECT(chargebackId)}`, {
    rejectionReasonId,
    rejectedBy,
    rejectionDetails
  }, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const processChargebackBatch = async ({ chargebackIds, action }) => {
  const response = await axios.post(`${API_BASE_URL}${API_ENDPOINTS.CHARGEBACKS}/bulk-process`, {
    chargebackIds,
    action
  }, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const getChargebackValidationHistory = async (chargebackId) => {
  const response = await axios.get(`${API_BASE_URL}${API_ENDPOINTS.CHARGEBACK_DETAIL(chargebackId)}/validation-history`, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const exportChargebacks = async (params = {}) => {
  const response = await axios.get(`${API_BASE_URL}${API_ENDPOINTS.CHARGEBACKS}/export`, {
    headers: getAuthHeaders(),
    params,
    responseType: 'blob'
  });
  return response.data;
};

export const exportChargebackReport = async ({ chargebackId }) => {
  const response = await axios.get(`${API_BASE_URL}${API_ENDPOINTS.CHARGEBACK_DETAIL(chargebackId)}/export-report`, {
    headers: getAuthHeaders(),
    responseType: 'blob'
  });
  return response.data;
};

// Enhanced Chargeback API functions
export const getChargebackWithValidationDetails = async (id) => {
  const response = await axios.get(`${API_BASE_URL}${API_ENDPOINTS.CHARGEBACK_DETAIL(id)}`, {
    headers: getAuthHeaders()
  });
  // Handle new API response format
  if (response.data.success && response.data.data) {
    return response.data.data;
  }
  return response.data;
};

// Fetch all rejection reasons with descriptions
export const fetchRejectionReasons = async () => {
  try {
    const response = await axios.get(`${API_BASE_URL}${API_ENDPOINTS.REJECTION_REASONS}`, {
      headers: getAuthHeaders()
    });
    const data = response.data;
    
    // Each reason includes description
    if (data.success && data.data && data.data.rejectionReasons) {
      data.data.rejectionReasons.forEach(reason => {
        console.log('Title:', reason.title);
        console.log('Description:', reason.description); // ← This is what you need
        console.log('Category:', reason.category);
        console.log('Subcategory:', reason.subcategory);
        console.log('Severity:', reason.severity);
        console.log('Resolution Steps:', reason.resolutionSteps);
        console.log('Business Impact:', reason.businessImpact);
        console.log('Compliance Notes:', reason.complianceNotes);
      });
      
      return data.data;
    }
    return null;
  } catch (error) {
    console.error('Error fetching rejection reasons:', error);
    throw error;
  }
};

// If you have a rejection reason ID
export const getRejectionReasonDescription = async (rejectionReasonId) => {
  try {
    const response = await axios.get(`${API_BASE_URL}${API_ENDPOINTS.REJECTION_REASONS}`, {
      headers: getAuthHeaders()
    });
    const data = response.data;
    
    if (data.success && data.data && data.data.rejectionReasons) {
      const reason = data.data.rejectionReasons.find(r => r.id === rejectionReasonId);
      if (reason) {
        return {
          title: reason.title,
          description: reason.description,
          resolutionSteps: reason.resolutionSteps,
          businessImpact: reason.businessImpact
        };
      }
    }
    return null;
  } catch (error) {
    console.error('Error fetching rejection reason:', error);
    throw error;
  }
};

// Get all descriptions for a specific category
export const getDescriptionsByCategory = async (category) => {
  try {
    const response = await axios.get(`${API_BASE_URL}${API_ENDPOINTS.REJECTION_REASONS}?category=${category}`, {
      headers: getAuthHeaders()
    });
    const data = response.data;
    
    if (data.success && data.data && data.data.rejectionReasons) {
      return data.data.rejectionReasons.map(reason => ({
        id: reason.id,
        title: reason.title,
        description: reason.description,
        subcategory: reason.subcategory
      }));
    }
    return [];
  } catch (error) {
    console.error('Error fetching descriptions:', error);
    throw error;
  }
};

// When you fetch a chargeback, it includes rejection reason details
export const fetchChargebackWithDescription = async (chargebackId) => {
  try {
    const response = await axios.get(`${API_BASE_URL}${API_ENDPOINTS.CHARGEBACK_DETAIL(chargebackId)}`, {
      headers: getAuthHeaders()
    });
    const data = response.data;
    
    const chargeback = data.data || data;
    if (chargeback.rejectionReasonDetail) {
      return {
        title: chargeback.rejectionReasonDetail.title,
        description: chargeback.rejectionReasonDetail.description,
        category: chargeback.rejectionReasonDetail.category,
        subcategory: chargeback.rejectionReasonDetail.subcategory,
        severity: chargeback.rejectionReasonDetail.severity,
        resolutionSteps: chargeback.rejectionReasonDetail.resolutionSteps,
        businessImpact: chargeback.rejectionReasonDetail.businessImpact,
        complianceNotes: chargeback.rejectionReasonDetail.complianceNotes
      };
    }
    return null;
  } catch (error) {
    console.error('Error fetching chargeback:', error);
    throw error;
  }
};

// Try to find rejection reason by searching through validation errors or other fields
export const findRejectionReasonByContent = async (searchText) => {
  try {
    const response = await axios.get(`${API_BASE_URL}${API_ENDPOINTS.REJECTION_REASONS}`, {
      headers: getAuthHeaders()
    });
    const data = response.data;
    
    if (data.success && data.data && data.data.rejectionReasons) {
      // Search through all rejection reasons for matches
      const matchingReasons = data.data.rejectionReasons.filter(reason => {
        const searchLower = searchText.toLowerCase();
        const titleLower = (reason.title || '').toLowerCase();
        const descLower = (reason.description || '').toLowerCase();
        const subcategoryLower = (reason.subcategory || '').toLowerCase(); // subcategory is nullable
        const categoryLower = (reason.category || '').toLowerCase();
        
        // Check for exact matches first
        if (titleLower.includes(searchLower) || descLower.includes(searchLower) || 
            subcategoryLower.includes(searchLower) || categoryLower.includes(searchLower)) {
          return true;
        }
        
        // Check for keyword matches (e.g., "price validation" matches "price" and "validation")
        const searchWords = searchLower.split(/\s+/);
        const reasonText = `${titleLower} ${descLower} ${subcategoryLower} ${categoryLower}`;
        
        return searchWords.every(word => reasonText.includes(word));
      });
      
      if (matchingReasons.length > 0) {
        console.log('🔍 Found matching rejection reasons:', matchingReasons);
        return matchingReasons[0]; // Return the first match
      }
    }
    return null;
  } catch (error) {
    console.error('Error searching rejection reasons:', error);
    throw error;
  }
};

export const getRejectionSubcategories = async () => {
  const response = await axios.get(`${API_BASE_URL}${API_ENDPOINTS.REJECTION_REASONS_SUBCATEGORIES}`, {
    headers: getAuthHeaders()
  });
  return response.data;
};

// Fetch chargebacks filtered by subcategory
export const fetchChargebacksBySubcategory = async (category, subcategory) => {
  try {
    const params = new URLSearchParams();
    if (category) params.append('rejectionCategory', category);
    if (subcategory) params.append('rejectionSubcategory', subcategory);
    
    const response = await axios.get(`${API_BASE_URL}${API_ENDPOINTS.CHARGEBACKS}?${params}`, {
      headers: getAuthHeaders()
    });
    const data = response.data;
    
    // Each chargeback will have rejectionReasonDetail with subcategory
    if (data.data && data.data.chargebacks) {
      data.data.chargebacks.forEach(chargeback => {
        if (chargeback.rejectionReasonDetail) {
          console.log('Subcategory:', chargeback.rejectionReasonDetail.subcategory);
          console.log('Category:', chargeback.rejectionReasonDetail.category);
        }
      });
    }
    
    return data.data;
  } catch (error) {
    console.error('Error fetching chargebacks:', error);
    throw error;
  }
};

// Fetch statistics grouped by subcategory
export const fetchSubcategoryStats = async (dateRange = '30d') => {
  try {
    const response = await axios.get(`${API_BASE_URL}${API_ENDPOINTS.CHARGEBACKS}/stats/by-subcategory?dateRange=${dateRange}`, {
      headers: getAuthHeaders()
    });
    const data = response.data;
    
    // Access subcategory statistics
    console.log('Subcategory stats:', data.data.subcategoryStats);
    console.log('Summary:', data.data.summary);
    
    return data.data;
  } catch (error) {
    console.error('Error fetching subcategory stats:', error);
    throw error;
  }
};

export const validateChargebackEnhanced = async (chargebackId) => {
  const response = await axios.post(`${API_BASE_URL}${API_ENDPOINTS.CHARGEBACK_VALIDATE(chargebackId)}`, {}, {
    headers: getAuthHeaders()
  });
  return response.data;
};

// Test Data API functions
export const generateTestData = async () => {
  const response = await axios.post(`${API_BASE_URL}/api/test-data/generate`, {}, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const getTestDataStatus = async () => {
  const response = await axios.get(`${API_BASE_URL}/api/test-data/status`, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const deleteAllData = async () => {
  const response = await axios.delete(`${API_BASE_URL}/api/test-data/delete-all`, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const resetAdminFunctions = async () => {
  const response = await axios.post(`${API_BASE_URL}/api/test-data/reset-admin`, {}, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const resetAllDataWithAdmin = async () => {
  const response = await axios.post(`${API_BASE_URL}/api/test-data/reset-all`, {}, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const getSalesDataFiles = async (params = {}) => {
  const queryParams = new URLSearchParams();
  if (params.page) queryParams.append('page', params.page);
  if (params.limit) queryParams.append('limit', params.limit);
  if (params.status) queryParams.append('status', params.status);
  if (params.uploadedBy) queryParams.append('uploadedBy', params.uploadedBy);
  if (params.archived) queryParams.append('archived', params.archived);

  const url = `${API_BASE_URL}${API_ENDPOINTS.SALES_DATA_FILES}${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
  
  const response = await axios.get(url, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const getSalesRecords = async (params = {}) => {
  const qp = new URLSearchParams();
  ['page', 'limit', 'fileId', 'customerId', 'productId', 'validationStatus', 'startDate', 'endDate', 'search', 'sortBy', 'sortOrder']
    .forEach((k) => { if (params[k] !== undefined && params[k] !== '' && params[k] !== 'ALL') qp.append(k, params[k]); });
  const url = `${API_BASE_URL}${API_ENDPOINTS.SALES_DATA_RECORDS}${qp.toString() ? '?' + qp.toString() : ''}`;
  const response = await axios.get(url, { headers: getAuthHeaders() });
  return response.data?.data || { records: [], pagination: {} };
};

export const deleteSalesDataFile = async (id) => {
  const r = await axios.delete(`${API_BASE_URL}/api/sales-data/files/${id}`, { headers: getAuthHeaders() });
  return r.data;
};

// Download an Excel/CSV export from a backend endpoint that streams the file.
// `path` is the full API path (e.g. '/api/customers/export'); `downloadName`
// is the filename saved to disk. Uses the sanitized API_BASE_URL so the /api
// prefix is always correct, and sends the auth token.
export const downloadExport = async (path, downloadName) => {
  const res = await fetch(`${API_BASE_URL}${path}`, { headers: getAuthHeaders() });
  if (!res.ok) throw new Error('Export failed');
  const blob = await res.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = downloadName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};

export const archiveSalesDataFile = async (id) => {
  const r = await axios.post(`${API_BASE_URL}/api/sales-data/files/${id}/archive`, {}, { headers: getAuthHeaders() });
  return r.data;
};

export const unarchiveSalesDataFile = async (id) => {
  const r = await axios.post(`${API_BASE_URL}/api/sales-data/files/${id}/unarchive`, {}, { headers: getAuthHeaders() });
  return r.data;
};

export const getSalesDataFileDetail = async (id) => {
  const response = await axios.get(`${API_BASE_URL}${API_ENDPOINTS.SALES_DATA_FILE_DETAIL(id)}`, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const getSalesDataRecords = async (params = {}) => {
  const queryParams = new URLSearchParams();
  if (params.page) queryParams.append('page', params.page);
  if (params.limit) queryParams.append('limit', params.limit);
  if (params.fileId) queryParams.append('fileId', params.fileId);
  if (params.customerId) queryParams.append('customerId', params.customerId);
  if (params.productId) queryParams.append('productId', params.productId);
  if (params.validationStatus) queryParams.append('validationStatus', params.validationStatus);
  if (params.startDate) queryParams.append('startDate', params.startDate);
  if (params.endDate) queryParams.append('endDate', params.endDate);
  
  const url = `${API_BASE_URL}${API_ENDPOINTS.SALES_DATA_RECORDS}${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
  
  const response = await axios.get(url, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const getSalesDataStatistics = async (params = {}) => {
  const queryParams = new URLSearchParams();
  if (params.fileId) queryParams.append('fileId', params.fileId);
  if (params.startDate) queryParams.append('startDate', params.startDate);
  if (params.endDate) queryParams.append('endDate', params.endDate);
  
  const url = `${API_BASE_URL}${API_ENDPOINTS.SALES_DATA_STATISTICS}${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
  
  const response = await axios.get(url, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const downloadSalesDataTemplate = async () => {
  const response = await axios.get(`${API_BASE_URL}${API_ENDPOINTS.SALES_DATA_TEMPLATE}`, {
    headers: getAuthHeaders(),
    responseType: 'blob'
  });
  
  const url = window.URL.createObjectURL(new Blob([response.data]));
  const a = document.createElement('a');
  a.href = url;
  a.download = 'sales-data-template.csv';
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
  document.body.removeChild(a);
  
  return { success: true, message: 'Template downloaded successfully' };
};

export const validateChargebackAgainstSalesData = async (chargebackId, options = {}) => {
  const response = await axios.post(`${API_BASE_URL}${API_ENDPOINTS.SALES_DATA_VALIDATE_CHARGEBACK}`, {
    chargebackId,
    options
  }, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const validateChargebacksBatch = async (chargebackIds, options = {}) => {
  const response = await axios.post(`${API_BASE_URL}${API_ENDPOINTS.SALES_DATA_VALIDATE_CHARGEBACKS_BATCH}`, {
    chargebackIds,
    options
  }, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const getValidationHistory = async (params = {}) => {
  const queryParams = new URLSearchParams();
  if (params.chargebackId) queryParams.append('chargebackId', params.chargebackId);
  if (params.fileId) queryParams.append('fileId', params.fileId);
  if (params.page) queryParams.append('page', params.page);
  if (params.limit) queryParams.append('limit', params.limit);
  if (params.validationType) queryParams.append('validationType', params.validationType);
  if (params.severity) queryParams.append('severity', params.severity);
  if (params.status) queryParams.append('status', params.status);
  
  const url = `${API_BASE_URL}${API_ENDPOINTS.SALES_DATA_VALIDATION_HISTORY}${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
  
  const response = await axios.get(url, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const getValidationStatistics = async (params = {}) => {
  const queryParams = new URLSearchParams();
  if (params.startDate) queryParams.append('startDate', params.startDate);
  if (params.endDate) queryParams.append('endDate', params.endDate);
  if (params.validationType) queryParams.append('validationType', params.validationType);
  
  const url = `${API_BASE_URL}${API_ENDPOINTS.SALES_DATA_VALIDATION_STATISTICS}${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
  
  const response = await axios.get(url, {
    headers: getAuthHeaders()
  });
  return response.data;
};




// Finance Export API functions
export const generateFinanceExport = async (batchId, format = "csv") => {
  const response = await axios.post(`${API_BASE_URL}/api/finance-exports`, {
    batchId,
    format
  }, {
    headers: getAuthHeaders()
  });
  
  return response.data;
};

export const getFinanceExportHistory = async (batchId) => {
  const response = await axios.get(`${API_BASE_URL}/api/finance-exports/batch/${batchId}`, {
    headers: getAuthHeaders()
  });
  
  return response.data;
};

export const downloadFinanceExport = async (exportId) => {
  const response = await axios.get(`${API_BASE_URL}/api/finance-exports/${exportId}/download`, {
    headers: getAuthHeaders(),
    responseType: "blob"
  });
  
  return response.data;
};

export const getFinanceExportDetails = async (exportId) => {
  const response = await axios.get(`${API_BASE_URL}/api/finance-exports/${exportId}`, {
    headers: getAuthHeaders()
  });
  
  return response.data;
};

export const deleteFinanceExport = async (exportId) => {
  const response = await axios.delete(`${API_BASE_URL}/api/finance-exports/${exportId}`, {
    headers: getAuthHeaders()
  });
  
  return response.data;
};

// ==================== GPO API FUNCTIONS ====================

export const getGpos = async (params = {}) => {
  const queryParams = new URLSearchParams();
  if (params.page) queryParams.append('page', params.page);
  if (params.limit) queryParams.append('limit', params.limit);
  if (params.search) queryParams.append('search', params.search);
  if (params.state) queryParams.append('state', params.state);
  if (params.country) queryParams.append('country', params.country);
  if (params.sortBy) queryParams.append('sortBy', params.sortBy);
  if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder);
  
  const url = `${API_BASE_URL}${API_ENDPOINTS.GPOS}${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
  
  const response = await axios.get(url, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const getGpo = async (gpoId) => {
  const response = await axios.get(`${API_BASE_URL}${API_ENDPOINTS.GPOS}/${gpoId}`, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const createGpo = async (gpoData) => {
  const response = await axios.post(`${API_BASE_URL}${API_ENDPOINTS.GPOS}`, gpoData, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const updateGpo = async (gpoId, gpoData) => {
  const response = await axios.put(`${API_BASE_URL}${API_ENDPOINTS.GPOS}/${gpoId}`, gpoData, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const deleteGpo = async (gpoId) => {
  const response = await axios.delete(`${API_BASE_URL}${API_ENDPOINTS.GPOS}/${gpoId}`, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const addGpoMember = async (gpoId, payload) => {
  const r = await axios.post(`${API_BASE_URL}${API_ENDPOINTS.GPOS}/${gpoId}/members`, payload, { headers: getAuthHeaders() });
  return r.data?.data;
};

export const removeGpoMember = async (gpoId, customerId) => {
  const r = await axios.delete(`${API_BASE_URL}${API_ENDPOINTS.GPOS}/${gpoId}/members/${customerId}`, { headers: getAuthHeaders() });
  return r.data;
};

export const getGpoContracts = async (gpoId, params = {}) => {
  const queryParams = new URLSearchParams();
  if (params.page) queryParams.append('page', params.page);
  if (params.limit) queryParams.append('limit', params.limit);
  if (params.status) queryParams.append('status', params.status);
  if (params.type) queryParams.append('type', params.type);
  
  const url = `${API_BASE_URL}${API_ENDPOINTS.GPOS}/${gpoId}/contracts${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
  
  const response = await axios.get(url, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const getGpoStatistics = async () => {
  const response = await axios.get(`${API_BASE_URL}${API_ENDPOINTS.GPOS}/statistics/overview`, {
    headers: getAuthHeaders()
  });
  return response.data;
};

// ============================================================================
// Government Pricing API
// ============================================================================
const govUnwrap = (r) => (r.data && r.data.data !== undefined ? r.data.data : r.data);

export const getGovPricingPeriods = async (params = {}) => {
  const qs = new URLSearchParams(Object.fromEntries(Object.entries(params).filter(([, v]) => v))).toString();
  const r = await axios.get(`${API_BASE_URL}/api/government-pricing/periods${qs ? `?${qs}` : ''}`, { headers: getAuthHeaders() });
  return govUnwrap(r);
};

export const getGovPricingPeriod = async (id) => {
  const r = await axios.get(`${API_BASE_URL}/api/government-pricing/periods/${id}`, { headers: getAuthHeaders() });
  return govUnwrap(r);
};

export const runGovPricingPeriod = async (payload) => {
  const r = await axios.post(`${API_BASE_URL}/api/government-pricing/periods/run`, payload, { headers: getAuthHeaders() });
  return govUnwrap(r);
};

export const certifyGovPricingPeriod = async (id) => {
  const r = await axios.post(`${API_BASE_URL}/api/government-pricing/periods/${id}/certify`, {}, { headers: getAuthHeaders() });
  return govUnwrap(r);
};

export const restateGovPricingPeriod = async (id, reason) => {
  const r = await axios.post(`${API_BASE_URL}/api/government-pricing/periods/${id}/restate`, { reason }, { headers: getAuthHeaders() });
  return govUnwrap(r);
};
export const deleteGovPricingPeriod = async (id) => {
  const r = await axios.delete(`${API_BASE_URL}/api/government-pricing/periods/${id}`, { headers: getAuthHeaders() });
  return r.data;
};
export const clearGovPricingPeriods = async (program) => {
  const r = await axios.post(`${API_BASE_URL}/api/government-pricing/periods/clear`, program ? { program } : {}, { headers: getAuthHeaders() });
  return r.data?.data || r.data;
};

export const getCpiuReferences = async () => {
  const r = await axios.get(`${API_BASE_URL}/api/government-pricing/cpiu`, { headers: getAuthHeaders() });
  return govUnwrap(r);
};

export const addCpiuReference = async (payload) => {
  const r = await axios.post(`${API_BASE_URL}/api/government-pricing/cpiu`, payload, { headers: getAuthHeaders() });
  return govUnwrap(r);
};

export const getGovExclusions = async () => {
  const r = await axios.get(`${API_BASE_URL}/api/government-pricing/exclusions`, { headers: getAuthHeaders() });
  return govUnwrap(r);
};

export const addGovExclusion = async (payload) => {
  const r = await axios.post(`${API_BASE_URL}/api/government-pricing/exclusions`, payload, { headers: getAuthHeaders() });
  return govUnwrap(r);
};

// ============================================================================
// Outbound EDI generation / download (849 response, 845 price authorization)
// ============================================================================
export const generateEdi849Preview = async (batchId) => {
  const r = await axios.post(`${API_BASE_URL}/api/edi/generate-849`, { batchId }, { headers: getAuthHeaders() });
  return r.data && r.data.data !== undefined ? r.data.data : r.data;
};

export const downloadEdiDocument = async (type, id, fallbackName) => {
  const r = await axios.get(`${API_BASE_URL}/api/edi/download/${type}/${id}`, { headers: getAuthHeaders(), responseType: 'blob' });
  const cd = r.headers['content-disposition'] || '';
  const m = cd.match(/filename="?([^"]+)"?/);
  const name = (m && m[1]) || fallbackName || `${type}.edi`;
  const url = window.URL.createObjectURL(new Blob([r.data]));
  const a = document.createElement('a');
  a.href = url; a.download = name;
  document.body.appendChild(a); a.click(); a.remove();
  window.URL.revokeObjectURL(url);
};

// --- EDI Transactions & Monitoring ---
export const getEdiTransactions = async (params = {}) => {
  const qs = new URLSearchParams(Object.fromEntries(Object.entries(params).filter(([, v]) => v))).toString();
  const r = await axios.get(`${API_BASE_URL}/api/edi/transactions${qs ? `?${qs}` : ''}`, { headers: getAuthHeaders() });
  return r.data?.data || { transactions: [], total: 0 };
};
export const getEdiMonitoring = async () => {
  const r = await axios.get(`${API_BASE_URL}/api/edi/monitoring`, { headers: getAuthHeaders() });
  return r.data?.data || {};
};

// --- Pricing change communication (EDI 845 notifications) ---
export const getPriceNotifications = async (params = {}) => {
  const qs = new URLSearchParams(params).toString();
  const r = await axios.get(`${API_BASE_URL}/api/edi/price-notifications${qs ? `?${qs}` : ''}`, { headers: getAuthHeaders() });
  return r.data?.data?.notifications || [];
};
export const communicatePricing = async (contractId) => {
  const r = await axios.post(`${API_BASE_URL}/api/edi/price-notifications/communicate`, { contractId }, { headers: getAuthHeaders() });
  return r.data?.data;
};

// --- Finance / AR: credit memos + gross-to-net ---
function triggerFileDownload(blob, contentDisposition, fallbackName = 'download.csv') {
  const m = /filename="?([^"]+)"?/.exec(contentDisposition || '');
  const name = (m && m[1]) || fallbackName;
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = name;
  document.body.appendChild(a); a.click(); a.remove();
  window.URL.revokeObjectURL(url);
}
export const downloadCreditMemo = async (id) => {
  const r = await axios.get(`${API_BASE_URL}/api/finance/credit-memos/${id}/download`, { headers: getAuthHeaders(), responseType: 'blob' });
  triggerFileDownload(r.data, r.headers['content-disposition'], 'credit-memo.csv');
};
export const exportCreditMemos = async (params = {}) => {
  const qs = new URLSearchParams(params).toString();
  const r = await axios.get(`${API_BASE_URL}/api/finance/credit-memos/export${qs ? '?' + qs : ''}`, { headers: getAuthHeaders(), responseType: 'blob' });
  triggerFileDownload(r.data, r.headers['content-disposition'], 'credit-memos.csv');
};
export const bulkCreateCreditMemos = async (payload) => {
  const r = await axios.post(`${API_BASE_URL}/api/finance/credit-memos/bulk`, payload, { headers: getAuthHeaders() });
  return r.data;
};
export const uploadProductsCSV = async (file) => {
  const fd = new FormData(); fd.append('csvFile', file);
  const r = await axios.post(`${API_BASE_URL}/api/products/upload-csv`, fd, { headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' } });
  return r.data;
};
export const downloadProductTemplate = async () => {
  const r = await axios.get(`${API_BASE_URL}/api/products/download-template`, { headers: getAuthHeaders(), responseType: 'blob' });
  triggerFileDownload(r.data, r.headers['content-disposition'], 'product-upload-template.csv');
};
export const uploadGposCSV = async (file) => {
  const fd = new FormData(); fd.append('csvFile', file);
  const r = await axios.post(`${API_BASE_URL}/api/gpos/upload-csv`, fd, { headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' } });
  return r.data;
};
export const downloadGpoTemplate = async () => {
  const r = await axios.get(`${API_BASE_URL}/api/gpos/download-template`, { headers: getAuthHeaders(), responseType: 'blob' });
  triggerFileDownload(r.data, r.headers['content-disposition'], 'gpo-upload-template.csv');
};
export const getCreditMemos = async (params = {}) => {
  const qs = new URLSearchParams(params).toString();
  const r = await axios.get(`${API_BASE_URL}/api/finance/credit-memos${qs ? `?${qs}` : ''}`, { headers: getAuthHeaders() });
  return r.data?.data || { memos: [], totals: {} };
};
export const createCreditMemo = async (payload) => {
  const r = await axios.post(`${API_BASE_URL}/api/finance/credit-memos`, payload, { headers: getAuthHeaders() });
  return r.data?.data;
};
export const getCreditMemoDetail = async (id) => {
  const r = await axios.get(`${API_BASE_URL}/api/finance/credit-memos/${id}`, { headers: getAuthHeaders() });
  return r.data?.data;
};
export const createCreditMemoFromClaim = async (claimId) => {
  const r = await axios.post(`${API_BASE_URL}/api/finance/credit-memos/from-claim/${claimId}`, {}, { headers: getAuthHeaders() });
  return r.data?.data;
};
export const issueCreditMemo = async (id) => {
  const r = await axios.post(`${API_BASE_URL}/api/finance/credit-memos/${id}/issue`, {}, { headers: getAuthHeaders() });
  return r.data;
};
export const approveCreditMemo = async (id) => {
  const r = await axios.post(`${API_BASE_URL}/api/finance/credit-memos/${id}/approve`, {}, { headers: getAuthHeaders() });
  return r.data?.data;
};
export const rejectCreditMemo = async (id, reason) => {
  const r = await axios.post(`${API_BASE_URL}/api/finance/credit-memos/${id}/reject`, { reason }, { headers: getAuthHeaders() });
  return r.data?.data;
};
export const applyCreditMemo = async (id) => {
  const r = await axios.post(`${API_BASE_URL}/api/finance/credit-memos/${id}/apply`, {}, { headers: getAuthHeaders() });
  return r.data?.data;
};
export const reverseCreditMemo = async (id, reason) => {
  const r = await axios.post(`${API_BASE_URL}/api/finance/credit-memos/${id}/reverse`, { reason }, { headers: getAuthHeaders() });
  return r.data?.data;
};
export const voidCreditMemo = async (id) => {
  const r = await axios.post(`${API_BASE_URL}/api/finance/credit-memos/${id}/void`, {}, { headers: getAuthHeaders() });
  return r.data?.data;
};
export const getGrossToNet = async (months) => {
  const qs = months ? `?months=${months}` : '';
  const r = await axios.get(`${API_BASE_URL}/api/finance/gross-to-net${qs}`, { headers: getAuthHeaders() });
  return r.data?.data;
};
// --- A/R Reconciliation ---
export const getRemittances = async () => {
  const r = await axios.get(`${API_BASE_URL}/api/finance/remittances`, { headers: getAuthHeaders() });
  return r.data?.data?.remittances || [];
};
export const simulateRemittance = async () => {
  const r = await axios.post(`${API_BASE_URL}/api/finance/remittances/simulate`, {}, { headers: getAuthHeaders() });
  return r.data?.data;
};
export const getDeductions = async (params = {}) => {
  const qs = new URLSearchParams(Object.fromEntries(Object.entries(params).filter(([, v]) => v))).toString();
  const r = await axios.get(`${API_BASE_URL}/api/finance/deductions${qs ? `?${qs}` : ''}`, { headers: getAuthHeaders() });
  return r.data?.data || { deductions: [], totals: {} };
};
export const autoMatchDeductions = async () => {
  const r = await axios.post(`${API_BASE_URL}/api/finance/deductions/auto-match`, {}, { headers: getAuthHeaders() });
  return r.data?.data;
};
export const matchDeduction = async (id, creditMemoId) => {
  const r = await axios.post(`${API_BASE_URL}/api/finance/deductions/${id}/match`, { creditMemoId }, { headers: getAuthHeaders() });
  return r.data?.data;
};
export const disputeDeduction = async (id, reason) => {
  const r = await axios.post(`${API_BASE_URL}/api/finance/deductions/${id}/dispute`, { reason }, { headers: getAuthHeaders() });
  return r.data?.data;
};
export const writeoffDeduction = async (id) => {
  const r = await axios.post(`${API_BASE_URL}/api/finance/deductions/${id}/writeoff`, {}, { headers: getAuthHeaders() });
  return r.data?.data;
};
export const exportArReconciliation = async () => {
  const r = await axios.get(`${API_BASE_URL}/api/finance/ar-reconciliation`, { headers: getAuthHeaders(), responseType: 'blob' });
  const cd = r.headers['content-disposition'] || '';
  const name = (cd.match(/filename="?([^"]+)"?/) || [])[1] || `ar-reconciliation-${new Date().toISOString().slice(0, 10)}.csv`;
  const url = window.URL.createObjectURL(new Blob([r.data], { type: 'text/csv' }));
  const a = document.createElement('a'); a.href = url; a.download = name;
  document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
};

export const exportGlPostings = async (params = {}) => {
  const clean = Object.fromEntries(Object.entries(params).filter(([, v]) => v));
  const qs = new URLSearchParams(clean).toString();
  const r = await axios.get(`${API_BASE_URL}/api/finance/gl-postings${qs ? `?${qs}` : ''}`, { headers: getAuthHeaders(), responseType: 'blob' });
  const cd = r.headers['content-disposition'] || '';
  const name = (cd.match(/filename="?([^"]+)"?/) || [])[1] || `gl-gtn-postings-${new Date().toISOString().slice(0, 10)}.csv`;
  const url = window.URL.createObjectURL(new Blob([r.data], { type: 'text/csv' }));
  const a = document.createElement('a'); a.href = url; a.download = name;
  document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
};

// --- EDI Trading Partners (Configurations) ---
export const getEdiPartners = async () => {
  const r = await axios.get(`${API_BASE_URL}/api/edi/partners`, { headers: getAuthHeaders() });
  return r.data?.data?.partners || [];
};
export const createEdiPartner = async (payload) => {
  const r = await axios.post(`${API_BASE_URL}/api/edi/partners`, payload, { headers: getAuthHeaders() });
  return r.data?.data;
};
export const updateEdiPartner = async (id, payload) => {
  const r = await axios.put(`${API_BASE_URL}/api/edi/partners/${id}`, payload, { headers: getAuthHeaders() });
  return r.data?.data;
};
export const deleteEdiPartner = async (id) => {
  const r = await axios.delete(`${API_BASE_URL}/api/edi/partners/${id}`, { headers: getAuthHeaders() });
  return r.data;
};

// --- EDI Data Mappings ---
export const getEdiMappings = async (params = {}) => {
  const qs = new URLSearchParams(params).toString();
  const r = await axios.get(`${API_BASE_URL}/api/edi/mappings${qs ? `?${qs}` : ''}`, { headers: getAuthHeaders() });
  return r.data?.data?.mappings || [];
};
export const createEdiMapping = async (payload) => {
  const r = await axios.post(`${API_BASE_URL}/api/edi/mappings`, payload, { headers: getAuthHeaders() });
  return r.data?.data;
};
export const updateEdiMapping = async (id, payload) => {
  const r = await axios.put(`${API_BASE_URL}/api/edi/mappings/${id}`, payload, { headers: getAuthHeaders() });
  return r.data?.data;
};
export const deleteEdiMapping = async (id) => {
  const r = await axios.delete(`${API_BASE_URL}/api/edi/mappings/${id}`, { headers: getAuthHeaders() });
  return r.data;
};

// --- Company Profile (single-tenant identity) ---
export const getCompanyProfile = async () => {
  const r = await axios.get(`${API_BASE_URL}/api/company/profile`, { headers: getAuthHeaders() });
  return r.data?.data || {};
};
export const updateCompanyProfile = async (payload) => {
  const r = await axios.put(`${API_BASE_URL}/api/company/profile`, payload, { headers: getAuthHeaders() });
  return r.data?.data;
};

// --- Platform Invoices (subscription billing for the single company tenant) ---
export const getBillingProfile = async () => {
  const r = await axios.get(`${API_BASE_URL}/api/finance/billing-profile`, { headers: getAuthHeaders() });
  return r.data?.data || {};
};
export const updateBillingProfile = async (payload) => {
  const r = await axios.put(`${API_BASE_URL}/api/finance/billing-profile`, payload, { headers: getAuthHeaders() });
  return r.data?.data;
};
export const getPlatformInvoices = async (params = {}) => {
  const qs = new URLSearchParams(params).toString();
  const r = await axios.get(`${API_BASE_URL}/api/finance/invoices${qs ? `?${qs}` : ''}`, { headers: getAuthHeaders() });
  return r.data?.data || { invoices: [], summary: {} };
};
export const getInvoiceTiers = async () => {
  const r = await axios.get(`${API_BASE_URL}/api/finance/invoices/tiers`, { headers: getAuthHeaders() });
  return r.data?.data || {};
};
export const getBillingSummary = async (period) => {
  const qs = period ? `?period=${encodeURIComponent(period)}` : '';
  const r = await axios.get(`${API_BASE_URL}/api/finance/billing-summary${qs}`, { headers: getAuthHeaders() });
  return r.data?.data || null;
};

// Feature flags (optional modules, per company)
export const getFeatureFlags = async () => {
  const r = await axios.get(`${API_BASE_URL}/api/company/feature-flags`, { headers: getAuthHeaders() });
  return r.data?.data || {};
};
export const updateFeatureFlags = async (payload) => {
  const r = await axios.put(`${API_BASE_URL}/api/company/feature-flags`, payload, { headers: getAuthHeaders() });
  return r.data?.data || {};
};

// IRA Medicare inflation rebates
export const listIraPeriods = async (program) => {
  const qs = program ? `?program=${encodeURIComponent(program)}` : '';
  const r = await axios.get(`${API_BASE_URL}/api/ira-rebates/periods${qs}`, { headers: getAuthHeaders() });
  return r.data?.data || [];
};
export const getIraPeriod = async (id) => {
  const r = await axios.get(`${API_BASE_URL}/api/ira-rebates/periods/${id}`, { headers: getAuthHeaders() });
  return r.data?.data || null;
};
export const runIraPeriod = async (payload) => {
  const r = await axios.post(`${API_BASE_URL}/api/ira-rebates/periods/run`, payload, { headers: getAuthHeaders() });
  return r.data?.data;
};
export const certifyIraPeriod = async (id) => {
  const r = await axios.post(`${API_BASE_URL}/api/ira-rebates/periods/${id}/certify`, {}, { headers: getAuthHeaders() });
  return r.data?.data;
};
export const updateTierPricing = async (payload) => {
  const r = await axios.put(`${API_BASE_URL}/api/finance/tier-pricing`, payload, { headers: getAuthHeaders() });
  return r.data?.data;
};
export const getPlatformInvoice = async (id) => {
  const r = await axios.get(`${API_BASE_URL}/api/finance/invoices/${id}`, { headers: getAuthHeaders() });
  return r.data?.data;
};
export const generateInvoice = async (payload) => {
  const r = await axios.post(`${API_BASE_URL}/api/finance/invoices/generate`, payload, { headers: getAuthHeaders() });
  return r.data?.data;
};
export const generateBulkInvoices = async (payload = {}) => {
  const r = await axios.post(`${API_BASE_URL}/api/finance/invoices/generate-bulk`, payload, { headers: getAuthHeaders() });
  return r.data?.data;
};
export const setInvoiceStatus = async (id, action) => {
  const r = await axios.post(`${API_BASE_URL}/api/finance/invoices/${id}/${action}`, {}, { headers: getAuthHeaders() });
  return r.data?.data;
};
export const deletePlatformInvoice = async (id) => {
  const r = await axios.delete(`${API_BASE_URL}/api/finance/invoices/${id}`, { headers: getAuthHeaders() });
  return r.data;
};

// --- Trash (soft-deleted records; admin purge) ---
export const getTrash = async () => {
  const r = await axios.get(`${API_BASE_URL}/api/admin/trash`, { headers: getAuthHeaders() });
  return r.data?.data || { counts: {}, customers: [], products: [], contracts: [] };
};
export const restoreTrashItem = async (type, id) => {
  const r = await axios.post(`${API_BASE_URL}/api/admin/trash/restore`, { type, id }, { headers: getAuthHeaders() });
  return r.data;
};
export const purgeTrash = async () => {
  const r = await axios.post(`${API_BASE_URL}/api/admin/trash/purge`, {}, { headers: getAuthHeaders() });
  return r.data;
};

// --- Problem Reports (in-app "Report Problem") ---
export const submitProblemReport = async (payload) => {
  const r = await axios.post(`${API_BASE_URL}/api/problem-reports`, payload, { headers: getAuthHeaders() });
  return r.data;
};
export const getProblemReports = async (params = {}) => {
  const qs = new URLSearchParams(
    Object.entries(params).filter(([, v]) => v !== undefined && v !== '' && v !== 'ALL')
  ).toString();
  const r = await axios.get(`${API_BASE_URL}/api/problem-reports${qs ? `?${qs}` : ''}`, { headers: getAuthHeaders() });
  return r.data?.data || { reports: [], openCount: 0, pagination: {} };
};
export const updateProblemReport = async (id, payload) => {
  const r = await axios.patch(`${API_BASE_URL}/api/problem-reports/${id}`, payload, { headers: getAuthHeaders() });
  return r.data?.data;
};

// --- Chargeback archive ---
export const archiveChargeback = async (id) => {
  const r = await axios.post(`${API_BASE_URL}/api/chargebacks/${id}/archive`, {}, { headers: getAuthHeaders() });
  return r.data;
};
export const unarchiveChargeback = async (id) => {
  const r = await axios.post(`${API_BASE_URL}/api/chargebacks/${id}/unarchive`, {}, { headers: getAuthHeaders() });
  return r.data;
};
export const archiveChargebacksBulk = async (ids, archived = true) => {
  const r = await axios.post(`${API_BASE_URL}/api/chargebacks/archive-bulk`, { ids, archived }, { headers: getAuthHeaders() });
  return r.data;
};
